/* eslint-disable react/jsx-props-no-spreading */
import React from "react";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Box, TextField, Button, Grid, Typography, Divider, Alert } from "@mui/material";

import { notificationAdd } from "../../../../actions/notification";
import { updateNepalPayDynamicQrData } from "../../../../actions/resourceCentre";
import { ResourceCentre } from "../../../../interfaces/ResourceCentreInterface";
import {
  IDynamicQrPaymentInfoMetadata,
  INepalPayDynamicQrFormData,
  INepalPayDynamicQrData
} from "../../../../interfaces/QrPayment";

interface FieldConfig<T extends PropertyKey> {
  name: T;
  label: string;
  subLabel: string;
  type: string;
  // @NOTE: required determines if it is required for dynamic qr payment,
  // not if it is required in form
  required: boolean;
  statusKey?: keyof IDynamicQrPaymentInfoMetadata["nepalPay"];
  infoText?: (configured: boolean) => string;
}

export const NepalPayFields: FieldConfig<keyof INepalPayDynamicQrData>[] = [
  {
    name: "userId",
    label: "User ID",
    subLabel: "Username provided by Nepal pay for user authentication.",
    required: true,
    type: "text"
  },
  {
    name: "userName",
    label: "Username",
    subLabel: "Username provided by Nepal pay for basic authentication.",
    type: "text",
    required: true
  },
  {
    name: "acquirerId",
    label: "Acquirer ID",
    subLabel: "Numeric ID assigned to the acquirer",
    type: "text",
    required: true
  },
  {
    name: "merchantId",
    label: "Merchant ID",
    subLabel: "Unique ID of the merchant",
    type: "text",
    required: true
  },
  {
    name: "merchantName",
    label: "Merchant Name",
    subLabel: "Name of your business or store",
    type: "text",
    required: true
  },
  {
    name: "merchantCity",
    label: "Merchant City",
    subLabel: "City where the merchant is located",
    type: "text",
    required: true
  },
  {
    name: "merchantCategoryCode",
    label: "Merchant Category Code",
    subLabel: "Industry classification code",
    type: "number",
    required: true
  },
  {
    name: "valueOfConvenienceFeeFixed",
    label: "Convenience Fee (Transaction Cost)",
    subLabel: "Specify a fixed convenience fee per transaction",
    type: "number",
    required: false
  },
  {
    name: "purposeOfTransaction",
    label: "Purpose of Transaction",
    subLabel: "Describe the transaction purpose",
    type: "text",
    required: false
  },
  {
    name: "merchantPostalCode",
    label: "Merchant Postal Code",
    subLabel: "Postal code of the merchant's location",
    type: "number",
    required: true
  },
  {
    name: "apiKey",
    label: "Merchant API Key",
    subLabel: "API Key provided by Nepal Pay",
    type: "text",
    required: true
  },
  {
    name: "publicKey",
    label: "Merchant Public Key",
    subLabel: "Public key provided by Nepal Pay",
    type: "text",
    required: true
  }
];

export const NepalPaySecretFields: FieldConfig<"password" | "privateKey">[] = [
  {
    name: "password",
    label: "Password",
    subLabel: "Password provided by Nepal pay for basic authentication.",
    type: "text",
    required: false,
    statusKey: "hasPassword",
    infoText: (configured) =>
      configured
        ? "A password is already configured. Enter a new password to replace it."
        : "No password has been configured."
  }
];

function NepalPayForm({ resourceCentre }: { resourceCentre: ResourceCentre }): React.ReactNode {
  const dispatch = useDispatch();
  const metadata = resourceCentre?.dynamicQrPaymentInfoMetadata ?? {};

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<INepalPayDynamicQrFormData>({
    defaultValues: {
      userId: resourceCentre?.dynamicQrPaymentInfo?.nepalPay?.userId || "",
      userName: resourceCentre?.dynamicQrPaymentInfo?.nepalPay?.userName || "",
      acquirerId: resourceCentre?.dynamicQrPaymentInfo?.nepalPay?.acquirerId || "",
      merchantId: resourceCentre?.dynamicQrPaymentInfo?.nepalPay?.merchantId || "",
      merchantName: resourceCentre?.dynamicQrPaymentInfo?.nepalPay?.merchantName || "",
      merchantCategoryCode:
        resourceCentre?.dynamicQrPaymentInfo?.nepalPay?.merchantCategoryCode || "",
      merchantCity: resourceCentre?.dynamicQrPaymentInfo?.nepalPay?.merchantCity || "",
      merchantPostalCode: resourceCentre?.dynamicQrPaymentInfo?.nepalPay?.merchantPostalCode || "",
      purposeOfTransaction:
        resourceCentre?.dynamicQrPaymentInfo?.nepalPay?.purposeOfTransaction || "Bill payment",
      valueOfConvenienceFeeFixed:
        resourceCentre?.dynamicQrPaymentInfo?.nepalPay?.valueOfConvenienceFeeFixed ?? undefined,
      publicKey: resourceCentre?.dynamicQrPaymentInfo?.nepalPay?.publicKey || "",
      apiKey: resourceCentre?.dynamicQrPaymentInfo?.nepalPay?.apiKey || "",
      privateKey: "",
      password: ""
    }
  });

  const onSubmit = async (data: INepalPayDynamicQrFormData) => {
    if (!resourceCentre?.id) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          autoTimeout: true,
          message: "Resource centre ID not found"
        })
      );

      return;
    }

    const payload: Partial<INepalPayDynamicQrFormData> = {
      ...data
    };

    if (!payload.password?.trim()) {
      delete payload.password;
    }

    if (!payload.privateKey?.trim()) {
      delete payload.privateKey;
    }

    try {
      await dispatch(updateNepalPayDynamicQrData(Number(resourceCentre?.id), data));
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "success",
          autoTimeout: true,
          message: "Nepal Pay settings updated successfully"
        })
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          autoTimeout: true,
          message: err?.message || err?.data?.message || "Failed to update settings"
        })
      );
    }
  };

  return (
    <Box>
      <Box sx={{ p: 2 }}>
        <Typography component="h3" sx={{ fontWeight: "bold" }}>
          NepalPay Configuration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Enter your merchant credentials and business details to enable NepalPay QR payments.
        </Typography>
      </Box>

      <Divider />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ p: 2 }}>
          {NepalPayFields.map(({ name, label, subLabel, type, statusKey, infoText }) => (
            <React.Fragment key={name}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Typography variant="body1" component="label" htmlFor={name}>
                  {label}
                </Typography>
                <Typography variant="caption" component="div" color="text.secondary">
                  {subLabel}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 9 }}>
                <Controller
                  name={name}
                  control={control}
                  render={({ field }) => {
                    const secretValue = statusKey ? metadata?.nepalPay?.[statusKey] : false;

                    return (
                      <TextField
                        {...field}
                        fullWidth
                        id={name}
                        type={type}
                        error={!!errors[name]}
                        variant="outlined"
                        placeholder={label}
                        label={label}
                        onFocus={(e) => e.target.select()}
                        slotProps={{ inputLabel: { shrink: true } }}
                        helperText={errors[name]?.message ?? infoText?.(secretValue)}
                      />
                    );
                  }}
                />
              </Grid>
            </React.Fragment>
          ))}

          {/* secret fields */}
          {NepalPaySecretFields.map(({ name, label, subLabel, type, statusKey, infoText }) => (
            <React.Fragment key={name}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Typography variant="body1" component="label" htmlFor={name}>
                  {label}
                </Typography>
                <Typography variant="caption" component="div" color="text.secondary">
                  {subLabel}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 9 }}>
                <Controller
                  name={name}
                  control={control}
                  render={({ field }) => {
                    const secretValue = statusKey ? metadata?.nepalPay?.[statusKey] : false;

                    return (
                      <TextField
                        {...field}
                        fullWidth
                        id={name}
                        type={type}
                        error={!!errors[name]}
                        variant="outlined"
                        placeholder={label}
                        label={label}
                        onFocus={(e) => e.target.select()}
                        slotProps={{ inputLabel: { shrink: true } }}
                        helperText={errors[name]?.message ?? infoText?.(secretValue)}
                      />
                    );
                  }}
                />
              </Grid>
            </React.Fragment>
          ))}

          {/* pem file input */}
          <Grid container spacing={2} size={12}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body1" component="label" htmlFor="pemFile">
                PEM File
              </Typography>
              <Typography variant="caption" component="div" color="text.secondary">
                Upload your private key PEM file
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 9 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {metadata?.nepalPay?.hasPrivateKey && (
                  <Alert severity="info">
                    {resourceCentre.dynamicQrPaymentInfoMetadata.nepalPay.hasPrivateKey
                      ? "A private key is already configured. Upload a new PEM file to replace it."
                      : "No private key has been configured yet."}
                  </Alert>
                )}

                <input
                  type="file"
                  accept=".pem"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (evt) => {
                        const fileContent = evt.target?.result;
                        if (typeof fileContent === "string") {
                          setValue("privateKey", fileContent);
                        }
                      };
                      reader.readAsText(file);
                    } else {
                      setValue("privateKey", "");
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ p: 2, display: "flex", ml: "auto" }}>
          <Button type="submit" variant="contained" color="primary" sx={{ ml: "auto" }}>
            Save Nepal Pay Configuration
          </Button>
        </Box>
      </form>
    </Box>
  );
}

export default NepalPayForm;
